import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCardWithPieChart from '@/components/dashboard/StatCardWithPieChart';
import StatCardWithBlocks from '@/components/dashboard/StatCardWithBlocks';
import type { HistoricalStats } from '@/components/dashboard/StatCardWithBlocks';
import { faBullseye, faChartPie, faUpDown, faArrowTrendUp } from '@/fontawesome';
import type {
  RecommendationStats,
  PreviewCoreRecommendation,
  PreviewEarningsRecommendation,
  PreviewSwingTradeRecommendation,
  PreviewLongTermRecommendation
} from '@/models';
import { calculateOpenStatus } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';

// Swing and long-term trades share the fields needed for the historical stats
type FinishedTrade = PreviewSwingTradeRecommendation | PreviewLongTermRecommendation;

interface TradingRecommendationsStatsProps {
  coreRecommendations: PreviewCoreRecommendation[];
  earningsRecommendations: PreviewEarningsRecommendation[];
  swingTradeRecommendations: PreviewSwingTradeRecommendation[];
  longTermRecommendations: PreviewLongTermRecommendation[];
  finishedSwingTradeRecommendations: PreviewSwingTradeRecommendation[];
  finishedLongTermRecommendations: PreviewLongTermRecommendation[];
}

function computeHistoricalStats(
  finishedTrades: FinishedTrade[],
  stats: RecommendationStats | null
): HistoricalStats | null {
  if (finishedTrades.length === 0 && stats === null) return null;

  const yields = finishedTrades.map(t => t.yield);

  const averageYield = yields.length > 0
    ? yields.reduce((sum, y) => sum + y, 0) / yields.length
    : 0;

  const totalFinished = stats !== null ? (stats.success + stats.failed) : 0;
  const winRate = totalFinished > 0 ? stats!.success / totalFinished : 0;

  // Actual holding period of the trade - not the DB timestamps (`updated` is the last edit of any kind)
  const holdingDays = finishedTrades
    .filter(t => t.endTimestamp !== null)
    .map(t => {
      const start = new Date(t.startTimestamp).getTime();
      const end = new Date(t.endTimestamp!).getTime();
      return (end - start) / (1000 * 60 * 60 * 24);
    });
  const averageHoldingDays = holdingDays.length > 0
    ? holdingDays.reduce((sum, d) => sum + d, 0) / holdingDays.length
    : 0;

  const bestYield = yields.length > 0 ? Math.max(...yields) : 0;
  const worstYield = yields.length > 0 ? Math.min(...yields) : 0;

  return {
    averageYield,
    winRate,
    averageHoldingDays,
    bestYield,
    worstYield,
  };
}

export default function TradingRecommendationsStats({
  coreRecommendations = [],
  earningsRecommendations = [],
  swingTradeRecommendations = [],
  longTermRecommendations = [],
  finishedSwingTradeRecommendations = [],
  finishedLongTermRecommendations = []
}: TradingRecommendationsStatsProps) {
  const navigate = useNavigate();
  const [swingStats, setSwingStats] = useState<RecommendationStats | null>(null);
  const [longtermStats, setLongtermStats] = useState<RecommendationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const { hasFeature } = useFeatureAccess();
  const hasSwingAccess = hasFeature('swingTradeRecommendations');
  const hasLongtermAccess = hasFeature('longTermRecommendations');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const overview = await APIRoutes.getStatistics();
        // Free + Normal kombinieren (wie in SwingTradesPage)
        const swingFree = overview.statsFree.swingTradeRecommendationStats;
        const swingNormal = overview.statsNormal.swingTradeRecommendationStats;
        setSwingStats({
          total: swingFree.total + swingNormal.total,
          open: swingFree.open + swingNormal.open,
          success: swingFree.success + swingNormal.success,
          failed: swingFree.failed + swingNormal.failed
        });

        const ltFree = overview.statsFree.longTermRecommendationStats;
        const ltNormal = overview.statsNormal.longTermRecommendationStats;
        setLongtermStats({
          total: ltFree.total + ltNormal.total,
          open: ltFree.open + ltNormal.open,
          success: ltFree.success + ltNormal.success,
          failed: ltFree.failed + ltNormal.failed
        });
      } catch {
        // Silently fail - stats sind optional
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Simplified stats - count recommendations based on winLossDelta
  const getSimplifiedStats = (recommendations: (PreviewCoreRecommendation | PreviewEarningsRecommendation)[]) => {
    const stats = {
      durchgegangen: 0,
      knapp: 0,
      eingebucht: 0
    };

    recommendations.forEach(r => {
      const status = calculateOpenStatus(r.winLossDelta);
      if (status === 'GEHT_DURCH') {
        stats.durchgegangen++;
      } else if (status === 'WIRD_KNAPP') {
        stats.knapp++;
      } else if (status === 'WIRD_EINGEBUCHT') {
        stats.eingebucht++;
      }
    });

    return stats;
  };

  const coreData = getSimplifiedStats(coreRecommendations);
  const earningsData = getSimplifiedStats(earningsRecommendations);

  // Convert recommendations to minimal format needed by StatCardWithBlocks
  // Open trades use winLossPercentage, finished trades use yield
  const openSwingTrades = swingTradeRecommendations.map(r => ({
    id: r.id,
    state: r.state,
    winLossPercentage: r.winLossPercentage
  }));

  const finishedSwingTrades = finishedSwingTradeRecommendations.map(r => ({
    id: r.id,
    state: r.state,
    winLossPercentage: r.yield // Use yield for finished trades
  }));

  // Long-term trades have no winLossPercentage - their yield already reflects a full exit at the current price
  const openLongtermTrades = longTermRecommendations.map(r => ({
    id: r.id,
    state: r.state,
    winLossPercentage: r.yield
  }));

  const finishedLongtermTrades = finishedLongTermRecommendations.map(r => ({
    id: r.id,
    state: r.state,
    winLossPercentage: r.yield // Use yield for finished trades
  }));

  const swingHistoricalStats = useMemo(
    () => computeHistoricalStats(finishedSwingTradeRecommendations, swingStats),
    [finishedSwingTradeRecommendations, swingStats]
  );

  const longtermHistoricalStats = useMemo(
    () => computeHistoricalStats(finishedLongTermRecommendations, longtermStats),
    [finishedLongTermRecommendations, longtermStats]
  );

  return (
    <>
      {/* Kernstrategie */}
      <StatCardWithPieChart
        title="Kernstrategie"
        icon={faBullseye}
        data={coreData}
        loading={false}
        onClick={() => navigate('/signale/kernstrategie')}
        dataTour="core-card"
      />

      {/* Earnings Trades */}
      <StatCardWithPieChart
        title="Earnings Trades"
        icon={faChartPie}
        data={earningsData}
        loading={false}
        onClick={() => navigate('/signale/earning-trades')}
        dataTour="earnings-card"
      />

      {/* Swing Trades */}
      <StatCardWithBlocks
        title="Swing Trades"
        icon={faUpDown}
        openTrades={openSwingTrades}
        finishedTrades={finishedSwingTrades}
        stats={swingStats}
        statsLoading={statsLoading}
        loading={false}
        onClick={() => navigate('/signale/swing-trades')}
        dataTour="swing-card"
        defaultView={hasSwingAccess ? 'current' : 'historical'}
        historicalStats={swingHistoricalStats}
      />

      {/* Langzeit Trades */}
      <StatCardWithBlocks
        title="Langzeit-Trades"
        icon={faArrowTrendUp}
        openTrades={openLongtermTrades}
        finishedTrades={finishedLongtermTrades}
        stats={longtermStats}
        statsLoading={statsLoading}
        loading={false}
        onClick={() => navigate('/signale/langzeit-trades')}
        dataTour="longterm-card"
        defaultView={hasLongtermAccess ? 'current' : 'historical'}
        historicalStats={longtermHistoricalStats}
      />
    </>
  );
}
